import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown, X } from "lucide-react";

const PRESETS = [
  { id: "this_week", label: "This Week" },
  { id: "this_month", label: "This Month" },
  { id: "this_quarter", label: "This Quarter" },
  { id: "this_year", label: "This Year" },
  { id: "last_month", label: "Last Month" },
  { id: "last_quarter", label: "Last Quarter" },
  { id: "last_year", label: "Last Year" },
  { id: "all_time", label: "All Time" },
  { id: "custom", label: "Custom Range" },
];

function getPresetDates(presetId) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const dow = now.getDay();

  switch (presetId) {
    case "this_week": {
      const start = new Date(y, m, d - dow);
      return { start: toISO(start), end: toISO(now) };
    }
    case "this_month":
      return { start: toISO(new Date(y, m, 1)), end: toISO(now) };
    case "this_quarter": {
      const qStart = new Date(y, Math.floor(m / 3) * 3, 1);
      return { start: toISO(qStart), end: toISO(now) };
    }
    case "this_year":
      return { start: toISO(new Date(y, 0, 1)), end: toISO(now) };
    case "last_month": {
      const s = new Date(y, m - 1, 1);
      const e = new Date(y, m, 0);
      return { start: toISO(s), end: toISO(e) };
    }
    case "last_quarter": {
      const cq = Math.floor(m / 3);
      const lqStart = new Date(y, (cq - 1) * 3, 1);
      const lqEnd = new Date(y, cq * 3, 0);
      return { start: toISO(lqStart), end: toISO(lqEnd) };
    }
    case "last_year":
      return { start: toISO(new Date(y - 1, 0, 1)), end: toISO(new Date(y - 1, 11, 31)) };
    case "all_time":
      return { start: "", end: "" };
    default:
      return { start: "", end: "" };
  }
}

function toISO(d) {
  return d.toISOString().split("T")[0];
}

function formatLabel(presetId, startDate, endDate) {
  if (presetId === "custom" && startDate && endDate) {
    const s = new Date(startDate + "T00:00:00");
    const e = new Date(endDate + "T00:00:00");
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${fmt(s)} - ${fmt(e)}`;
  }
  const p = PRESETS.find((x) => x.id === presetId);
  return p?.label || "This Quarter";
}

export default function DateRangeFilter({ value, onChange, className = "" }) {
  const [open, setOpen] = useState(false);
  const [customStart, setCustomStart] = useState(value?.start || "");
  const [customEnd, setCustomEnd] = useState(value?.end || "");
  const ref = useRef(null);

  const preset = value?.preset || "this_quarter";

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectPreset = (id) => {
    if (id === "custom") {
      onChange({ preset: "custom", start: customStart, end: customEnd });
    } else {
      const dates = getPresetDates(id);
      onChange({ preset: id, start: dates.start, end: dates.end });
      setOpen(false);
    }
  };

  const applyCustom = () => {
    if (customStart && customEnd) {
      onChange({ preset: "custom", start: customStart, end: customEnd });
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        data-testid="date-range-filter-btn"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 h-9 px-3.5 rounded-lg text-xs font-semibold transition-all"
        style={{
          backgroundColor: open ? "#1B75BA" : "#F7F8FA",
          color: open ? "white" : "#3A3F5C",
          border: open ? "1px solid #1B75BA" : "1px solid #E8EAF0",
        }}
      >
        <Calendar className="w-3.5 h-3.5" />
        <span>{formatLabel(preset, value?.start, value?.end)}</span>
        <ChevronDown className="w-3 h-3" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
      </button>

      {open && (
        <div
          data-testid="date-range-dropdown"
          className="absolute right-0 top-11 z-50 bg-white rounded-xl p-2 w-64"
          style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.14)", border: "1px solid #E8EAF0" }}
        >
          <div className="space-y-0.5 mb-2">
            {PRESETS.filter((p) => p.id !== "custom").map((p) => (
              <button
                key={p.id}
                data-testid={`date-preset-${p.id}`}
                onClick={() => selectPreset(p.id)}
                className="w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all"
                style={
                  preset === p.id
                    ? { backgroundColor: "#1B75BA", color: "white" }
                    : { color: "#3A3F5C" }
                }
                onMouseEnter={(e) => { if (preset !== p.id) e.target.style.backgroundColor = "#F7F8FA"; }}
                onMouseLeave={(e) => { if (preset !== p.id) e.target.style.backgroundColor = "transparent"; }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="border-t pt-2 mt-1" style={{ borderColor: "#EEF0F4" }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2 px-1" style={{ color: "#9BA4B8" }}>Custom Range</p>
            <div className="flex gap-2 mb-2">
              <input
                data-testid="custom-start-date"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="flex-1 h-8 px-2 rounded-lg text-[11px]"
                style={{ border: "1px solid #E8EAF0", color: "#3A3F5C", outline: "none" }}
              />
              <input
                data-testid="custom-end-date"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="flex-1 h-8 px-2 rounded-lg text-[11px]"
                style={{ border: "1px solid #E8EAF0", color: "#3A3F5C", outline: "none" }}
              />
            </div>
            <button
              data-testid="apply-custom-range"
              onClick={applyCustom}
              disabled={!customStart || !customEnd}
              className="w-full h-8 rounded-lg text-xs font-semibold text-white disabled:opacity-40"
              style={{ backgroundColor: "#1B75BA" }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { getPresetDates, PRESETS };
